import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  Autocomplete,
  Paper,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

const FUEL_COLORS = {
  Solar: '#f59e0b',
  Wind: '#3b82f6',
  Gas: '#ef4444',
  Hydro: '#06b6d4',
  'Black Coal': '#374151',
  'Brown Coal': '#78350f',
  Battery: '#8b5cf6',
  Other: '#6b7280',
};

const REGION_LABELS = {
  NSW: 'New South Wales',
  VIC: 'Victoria',
  QLD: 'Queensland',
  SA: 'South Australia',
  TAS: 'Tasmania',
  NSW1: 'New South Wales',
  VIC1: 'Victoria',
  QLD1: 'Queensland',
  SA1: 'South Australia',
  TAS1: 'Tasmania',
};

const DEFAULT_TOP_N = 5;
const MIN_TOP_N = 1;
const MAX_TOP_N = 20;

const fmtCap = (mw) => (mw == null ? '—' : `${Number(mw).toFixed(1)} MW`);

const BenchmarkRow = ({ asset, benchmarks, topN, loading, allAssets, onChange, onRefetch, onTopNChange }) => {
  const fuelColor = FUEL_COLORS[asset.fuel_code] || FUEL_COLORS.Other;
  const region = REGION_LABELS[asset.region_id] || asset.region_id;

  const currentIds = useMemo(
    () => new Set((benchmarks || []).map((b) => b.asset_id)),
    [benchmarks]
  );

  const addOptions = useMemo(
    () => (allAssets || []).filter(
      (a) => a.asset_id !== asset.asset_id && !currentIds.has(a.asset_id)
    ),
    [allAssets, asset.asset_id, currentIds]
  );

  const handleRemove = (benchId) => {
    onChange((benchmarks || []).filter((b) => b.asset_id !== benchId));
  };

  const handleAdd = (_, picked) => {
    if (!picked) return;
    onChange([
      ...(benchmarks || []),
      { ...picked, source: 'user', cross_region: picked.region_id !== asset.region_id },
    ]);
  };

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, mb: 1.5, borderRadius: 2 }}
    >
      {/* Header: target asset */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Box
          sx={{
            width: 8, height: 8, borderRadius: '50%', bgcolor: fuelColor, flexShrink: 0,
          }}
        />
        <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', flex: 1 }}>
          {asset.name}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontFamily: "'JetBrains Mono', monospace" }}
        >
          {region} · {fmtCap(asset.capacity)} · {asset.fuel_code}
        </Typography>
        <TextField
          type="number"
          size="small"
          value={topN}
          onChange={(e) => {
            const n = parseInt(e.target.value, 10);
            if (!Number.isNaN(n)) onTopNChange(Math.max(MIN_TOP_N, Math.min(MAX_TOP_N, n)));
          }}
          inputProps={{ min: MIN_TOP_N, max: MAX_TOP_N, style: { width: 50, textAlign: 'center' } }}
          label="Top N"
          sx={{ width: 90 }}
        />
        <Tooltip title="Re-pick suggestions (keeps user-added)">
          <span>
            <IconButton size="small" onClick={onRefetch} disabled={loading}>
              {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Benchmark chips */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5, minHeight: 32 }}>
        {(benchmarks || []).length === 0 && !loading && (
          <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            No benchmarks selected
          </Typography>
        )}
        {(benchmarks || []).map((b) => (
          <Chip
            key={b.asset_id}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span>{b.name}</span>
                <Typography
                  component="span"
                  sx={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '0.65rem',
                    opacity: 0.7,
                    ml: 0.5,
                  }}
                >
                  {fmtCap(b.capacity)}
                </Typography>
                {b.cross_region && (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.6rem',
                      fontWeight: 600,
                      ml: 0.5,
                      px: 0.5,
                      borderRadius: 0.5,
                      bgcolor: 'rgba(245, 158, 11, 0.15)',
                      color: '#f59e0b',
                    }}
                  >
                    {b.region_id}
                  </Typography>
                )}
              </Box>
            }
            size="small"
            onDelete={() => handleRemove(b.asset_id)}
            sx={{
              bgcolor: `${FUEL_COLORS[b.fuel_code] || FUEL_COLORS.Other}12`,
              border: `1px solid ${FUEL_COLORS[b.fuel_code] || FUEL_COLORS.Other}30`,
              fontSize: '0.75rem',
              height: 26,
            }}
          />
        ))}
      </Box>

      {/* Add benchmark */}
      <Autocomplete
        size="small"
        options={addOptions}
        value={null}
        blurOnSelect
        clearOnBlur
        onChange={handleAdd}
        getOptionLabel={(o) => `${o.name} (${o.asset_id})`}
        groupBy={(o) => REGION_LABELS[o.region_id] || o.region_id}
        isOptionEqualToValue={(o, v) => o.asset_id === v.asset_id}
        renderInput={(params) => (
          <TextField {...params} label="Add a benchmark" placeholder="Search assets..." />
        )}
      />
    </Paper>
  );
};

const BenchmarkStep = ({ selectedAssets, allAssets, benchmarks, setBenchmarks }) => {
  const [topN, setTopN] = useState({});
  const [loading, setLoading] = useState({});

  const fetchPeers = useCallback(async (asset, n) => {
    setLoading((prev) => ({ ...prev, [asset.asset_id]: true }));
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/portfolios/assets/${asset.asset_id}/similar`,
        { params: { top_n: n } }
      );
      const peers = (res.data?.peers || []).map((p) => ({ ...p, source: 'auto' }));
      // Preserve user-added benchmarks across a refetch; only swap auto ones.
      setBenchmarks((prev) => {
        const existing = prev[asset.asset_id] || [];
        const userKept = existing.filter((b) => b.source === 'user');
        const userIds = new Set(userKept.map((b) => b.asset_id));
        const autoFresh = peers.filter((p) => !userIds.has(p.asset_id));
        return { ...prev, [asset.asset_id]: [...autoFresh, ...userKept] };
      });
    } catch {
      // Silent: row shows "No benchmarks selected", user can add manually.
    } finally {
      setLoading((prev) => ({ ...prev, [asset.asset_id]: false }));
    }
  }, [setBenchmarks]);

  // On mount or when the selected assets change, initialise topN and fetch
  // suggestions for any asset we haven't loaded yet. We never auto-overwrite
  // a row the user has already edited.
  useEffect(() => {
    const toFetch = selectedAssets.filter(
      (a) => benchmarks[a.asset_id] === undefined
    );
    if (toFetch.length === 0) return;
    const nextTopN = { ...topN };
    toFetch.forEach((a) => {
      if (nextTopN[a.asset_id] == null) nextTopN[a.asset_id] = DEFAULT_TOP_N;
    });
    setTopN(nextTopN);
    toFetch.forEach((a) => fetchPeers(a, nextTopN[a.asset_id] || DEFAULT_TOP_N));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssets]);

  return (
    <Box>
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: '0.85rem',
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          mb: 1,
        }}
      >
        Benchmarks per asset
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Suggested peers are same region · same fuel · 0.5x–2x capacity. Edit
        each list as needed, or skip to create without benchmarks.
      </Typography>

      {selectedAssets.map((asset) => (
        <BenchmarkRow
          key={asset.asset_id}
          asset={asset}
          benchmarks={benchmarks[asset.asset_id]}
          topN={topN[asset.asset_id] ?? DEFAULT_TOP_N}
          loading={!!loading[asset.asset_id]}
          allAssets={allAssets}
          onChange={(next) =>
            setBenchmarks((prev) => ({ ...prev, [asset.asset_id]: next }))
          }
          onRefetch={() => fetchPeers(asset, topN[asset.asset_id] ?? DEFAULT_TOP_N)}
          onTopNChange={(n) => {
            setTopN((prev) => ({ ...prev, [asset.asset_id]: n }));
            fetchPeers(asset, n);
          }}
        />
      ))}
    </Box>
  );
};

export default BenchmarkStep;
